import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NewReleasesIcon from "@mui/icons-material/NewReleases";

const CHANGELOG_VERSION = "1.0.4";
const STORAGE_KEY = "changelog_read_version";
export const CHANGELOG_CLOSED_EVENT = "gtr:changelog-closed";

export const isChangeLogPending = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== CHANGELOG_VERSION;
  } catch {
    return false;
  }
};

const ChangeLogDialog: React.FC = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const lastReadVersion = localStorage.getItem(STORAGE_KEY);
    if (lastReadVersion !== CHANGELOG_VERSION) {
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, CHANGELOG_VERSION);
    setOpen(false);
    window.dispatchEvent(new Event(CHANGELOG_CLOSED_EVENT));
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: "!tw-overflow-hidden !tw-rounded-2xl tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth",
        sx: {
          m: { xs: 1.5, sm: 3 },
          maxHeight: { xs: "calc(100dvh - 24px)", sm: "calc(100dvh - 48px)" }
        }
      }}
    >
      <DialogTitle
        className="tw-bg-gradient-to-r tw-from-gtr-teal-dark tw-to-gtr-teal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: { xs: 2, sm: 3 },
          py: 2,
          color: "#fff",
          fontFamily: "Prometo",
          fontWeight: 700
        }}
      >
        Novidades da Plataforma
        <Tooltip title="Fechar" arrow placement="left">
          <IconButton
            aria-label="Fechar novidades da plataforma"
            onClick={handleClose}
            className="tw-transition-all tw-duration-200 hover:tw-bg-white/10 hover:tw-rotate-90"
            sx={{ color: "#fff", flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, bgcolor: "rgb(var(--gtr-surface-panel))" }}>
        <Box
          sx={{
            width: "100%",
          }}
        >
          {/* CONTEÚDO */}
          <Box sx={{ p: { xs: 2.5, sm: 3 }, width: "100%" }}>
            {/* Versão */}
            <Box mb={3}>
              <Chip
                label={`Versão ${CHANGELOG_VERSION}`}
                size="small"
                sx={{
                  bgcolor: "#00B700",
                  color: "#fff",
                  fontFamily: "Prometo",
                  fontWeight: 700
                }}
              />
            </Box>

            {/* Novidades */}
            <Box mb={3}>
              <Typography
                variant="subtitle1"
                className="!tw-mb-3 !tw-flex !tw-items-center !tw-gap-2 !tw-font-prometo !tw-font-bold !tw-text-gtr-text"
              >
                <NewReleasesIcon fontSize="small" className="tw-text-gtr-green" />
                Novidades desta versão
              </Typography>

              <Box
                component="ul"
                className="tw-m-0 tw-grid tw-list-none tw-gap-2.5 tw-p-0"
              >
                {[
                  ["Alerta de tempo ocioso", "Ligue nas configurações e defina os minutos: o sino avisa quando um veículo fica parado com a ignição ligada além do limite, e a grade só deixa o veículo laranja depois desse tempo."],
                  ["Condutor sem acesso", "Alerta na grade e no sino quando um condutor sem vínculo com o veículo é identificado."],
                  ["Pontos de interesse", "Cadastre locais importantes, veja os pontos no mapa, compartilhe pelo WhatsApp e consulte os relatórios de pontos próximos dos veículos."],
                  ["Tour de introdução", "Um passo a passo pelos botões do mapa, pela grade e pelo menu lateral do rastreamento."],
                  ["Link público do mapa", "Compartilhe a posição dos veículos por um link, sem expor dados sensíveis."],
                  ["Veículo mais próximo", "Na busca de endereço, encontre o veículo mais perto e veja a rota até ele."],
                  ["Abastecimentos na grade", "Consulte os abastecimentos de cada veículo direto pela grade do rastreamento."],
                  ["Cadastros mais rápidos", "Placa, CPF do condutor e CEP preenchem os dados automaticamente."],
                  ["Checklist ampliado", "Novos itens de segurança, condições do veículo e rastreamento, com a lista do mais recente para o mais antigo."],
                  ["Folgas dos motoristas", "Defina mais de um dia de folga para cada motorista."],
                ].map(([title, description]) => (
                  <Box
                    component="li"
                    key={title}
                    className="tw-flex tw-gap-2.5 tw-rounded-lg tw-bg-gtr-surface-muted tw-p-3"
                  >
                    <Box className="tw-mt-1 tw-h-2 tw-w-2 tw-flex-none tw-rounded-full tw-bg-gtr-green" />
                    <Typography variant="body2" className="!tw-font-prometo !tw-leading-relaxed !tw-text-gtr-text-muted">
                      <Box component="span" className="tw-font-bold tw-text-gtr-text">
                        {title}:
                      </Box>{" "}
                      {description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Divider />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: "rgb(var(--gtr-surface-muted))" }}>
        <Button
          variant="contained"
          onClick={handleClose}
          className="tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 hover:tw-shadow-lg"
          sx={{
            minWidth: 112,
            bgcolor: "#00B700",
            fontFamily: "Prometo",
            fontWeight: 700,
            "&:hover": { bgcolor: "#00B700" }
          }}
        >
          Entendi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeLogDialog;
